import { getDefaultScreenerFacilityId } from '@/hooks/facility-selection/defaultScreenerView';
import {
  getFacilityTypeGroupOptions,
  getGenericAllLabelForType
} from '@/hooks/facility-selection/screenerFacilityTypeHelpers';
import { isValidFacilityType } from '@/hooks/facility-selection/useScreenerFacilityView';
import { FacilityPayload } from '@/hooks/useFacilitiesQuery';
import { UserModel } from '@/models/UserModel';
import { UserPayload } from '@/models/UserPayload';
import { FacilityFacilityTypeEnum } from '@/types/crm-screener';

const ALL_FACILITIES_OPTION_PREFIX = 'all:';

export interface DefaultScreenerViewOption {
  id: string;
  label: string;
}

export const getDefaultScreenerViewOptions = (
  facilities: FacilityPayload[],
  accessibleFacilityIds: ReadonlySet<string>
): DefaultScreenerViewOption[] => {
  const groupOptions = getFacilityTypeGroupOptions(
    facilities,
    accessibleFacilityIds
  );
  const isMultiType = groupOptions.length > 1;

  const allFacilitiesOptions = groupOptions.map((group) => ({
    id: `${ALL_FACILITIES_OPTION_PREFIX}${group.facilityType}`,
    label: isMultiType
      ? group.label
      : getGenericAllLabelForType(group.facilityType)
  }));

  const facilityOptions = facilities
    .filter((facility) => accessibleFacilityIds.has(facility.id))
    .map((facility) => ({
      id: facility.id,
      label: facility.name
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  return [...allFacilitiesOptions, ...facilityOptions];
};

export const getSelectedDefaultScreenerViewOptionId = (
  user: UserModel,
  options: DefaultScreenerViewOption[]
): string => {
  const facilityType = user.default_screener_facility_type;
  const candidates = [
    isValidFacilityType(facilityType)
      ? `${ALL_FACILITIES_OPTION_PREFIX}${facilityType}`
      : null,
    getDefaultScreenerFacilityId(user),
    user.facility_id
  ];

  return (
    candidates.find((candidate) =>
      options.some((option) => option.id === candidate)
    ) ??
    options.find(
      (option) => !option.id.startsWith(ALL_FACILITIES_OPTION_PREFIX)
    )?.id ??
    options[0]?.id ??
    ''
  );
};

export const parseDefaultScreenerViewOptionId = (
  optionId: string
): Pick<
  UserPayload,
  'default_screener_facility_id' | 'default_screener_facility_type'
> =>
  optionId.startsWith(ALL_FACILITIES_OPTION_PREFIX)
    ? {
        default_screener_facility_id: null,
        default_screener_facility_type: optionId.slice(
          ALL_FACILITIES_OPTION_PREFIX.length
        ) as FacilityFacilityTypeEnum
      }
    : {
        default_screener_facility_id: optionId,
        default_screener_facility_type: null
      };
